import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import { ensure } from '../../../../sharpr/ensure/ensure';

export function IntegrationServicenowAddEditRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.IntegrationServicenowAddEdit', {
		url: '/admin/integrations/servicenow/:integration_id',
		controllerAs: 'vm',
		controller: IntegrationServicenowAddEditCtrl,
		templateUrl: '/ng/hub/Integration/Servicenow/AddEdit/IntegrationServicenowAddEditCtrl.html?' + window.BUILD,
		permission: ['manage_servicenow'],
		resolve: {
			style: () => AssetManager.css('/ng/hub/Integration/Servicenow/AddEdit/IntegrationServicenowAddEditCtrl.css'),
			$title: () => 'IntegrationServicenowAddEdit page',
			users: ApiResourcify.endpoint('/users/search'),
		},
	});
}

export class IntegrationServicenowAddEditCtrl {
	constructor(Api, Identity, notifications, $stateParams, $timeout, users) {
		'ngInject';
		this.Api = Api;
		this.Identity = Identity;
		this.notifications = notifications;
		this.$timeout = $timeout;
		this.haveExistingIntegration = false;
		this.haveCredentials = false;
		this.verifyingCredentials = false;
		this.currentClient = this.Identity.currentClient();
		this.users = users.map(user => {
			user.$search = `${user.fname_preferred || user.fname} ${user.lname_preferred || user.lname} (${user.email})`;
			return user;
		});
		this.samplePost = {
			attachment_thumbnails: [],
			category: { id: 0, name: 'Uncategorized' },
			headline: '',
			intel_text: '',
			rich_text_body: '',
			published_at: '1970-01-01T00:00:00Z',
			published_by: {},
			source_name: 'ServiceNow',
			source_link: '',
		};
		this.mappingsSuccess = undefined;
		if ($stateParams.integration_id) {
			this.getExistingIntegration($stateParams.integration_id);
		}
	}

	_getUserObjectByUserId(userId) {
		let user = {};
		for (let i = 0; i < this.users.length; i++) {
			if (this.users[i].id == userId) {
				user = this.users[i];
				break;
			}
		}
		return user;
	}

	/**
	 * Query Sharpr to see if this client already has a ServiceNow integration. If so,
	 * the function fills in values in the page's setup and mappings forms.
	 */
	getExistingIntegration(integrationId) {
		this.Api.get(`/v2/credentials/${integrationId}`).then(credentials => {
			if (!credentials || credentials.length === 0) {
				return;
			}
			const details = ensure.object(credentials);
			this.haveCredentials = true;
			// we don't need these props
			for (let key of ['user_id', 'client_id', 'created', 'last_sync', 'next_sync']) {
				delete details[key];
			}
			this.integration = details;
			// `metadata` may not be set if the user quit the process before they saved the integration
			if (details.metadata) {
				this.integration.metadata = JSON.parse(details.metadata);
				delete details.metadata.last_imported_post_date;
				this.selectedSharprUser = this._getUserObjectByUserId(this.integration.metadata.mappings.published_by);
				this.existingDataOptionSelected = this.integration.metadata.hasOwnProperty('get_existing_servicenow_data');
				this.haveExistingIntegration = true;
				this.verifyServicenowConnection();
			} else {
				this.integration.metadata = {};
				this.selectedSharprUser = {};
				this.existingDataOptionSelected = true;
			}
		});
	}

	/**
	 * Make sure the user has chosen a ServiceNow column with date values.
	 * @param supposedlyADate String  Value to check for dateness
	 * @param column String  The column the user chose for the published_at attribute
	 */
	verifyDate(supposedlyADate, column) {
		if (moment(supposedlyADate).isValid()) {
			this.samplePost.published_at = supposedlyADate;
		} else {
			alert(`The ${column} column doesn\'t contain a date. Please choose a different ServiceNow column.`);
		}
	}

	/**
	 * Query the ServiceNow API (through Sharpr's API) to see if the provided credentials are valid. Displays an error
	 * message if not. Otherwise, fills in mappings select elements with names of columns in the ServiceNow table.
	 */
	verifyServicenowConnection() {
		this.verifyingCredentials = true;
		this.Api.get(`/v2/servicenow/${this.integration.id}`, {
			limit: 1,
			workflow_state: 'published',
		}).then(resp => {
			const metadata = this.integration.metadata;
			if (resp.status === 'failure') {
				this.servicenowConnectionStatus = `Could not connect to ${metadata.servicenow_subdomain}.service-now.com`;
				this.connectionError = resp.error.message;
			} else if (resp.length == 0) {
				// we have to have at least one ServiceNow article so we can get the keys
				this.servicenowConnectionStatus = `Connection to ${metadata.servicenow_subdomain}.service-now.com was successful. However, no ServiceNow articles were found. Please add at least one article to ServiceNow then click "UPDATE CONNECTION" above.`;
			} else {
				const post = resp[0];
				// the link is different if it's a kb_knowledge article
				let link = `https://${metadata.servicenow_subdomain}.service-now.com/`;
				if (metadata.servicenow_table == 'kb_knowledge') {
					link += `kb_view.do?sysparm_article=${post.number}`;
				} else {
					link += `nav_to.do?uri=${metadata.servicenow_table}.do?sys_id=${post.sys_id}`;
				}
				this.verifyingCredentials = false;
				this.servicenowConnectionStatus = `Connection to ${metadata.servicenow_subdomain}.service-now.com successful!`;
				this.servicenowPost = post;
				this.servicenowColumns = Object.keys(this.servicenowPost).sort();
				this.samplePost.source_link = link;
				this.samplePost.published_by = this.selectedSharprUser || this._getUserObjectByUserId(userId);

				// the user is editing an existing integration
				if (metadata.hasOwnProperty('mappings')) {
					// map the data from the ServiceNow post to the sample Sharpr post
					this.samplePost.intel_text = this.servicenowPost[metadata.mappings.intel_text_column];
					this.samplePost.headline = this.servicenowPost[metadata.mappings.headline_column];
					this.samplePost.rich_text_body = this.servicenowPost[metadata.mappings.body_column];
					this.samplePost.published_at = this.servicenowPost.sys_created_on;
					this.samplePost.source_name = metadata.mappings.source_name || 'ServiceNow';
					this.samplePost.published_by = this.selectedSharprUser;
				}
			}
		});
	}

	resetStatusMessages() {
		this.servicenowConnectionStatus = false;
		this.connectionError = false;
	}

	updateBodyText() {
		this.$timeout(() => {
			this.samplePost.rich_text_body = this.servicenowPost[this.integration.metadata.mappings.body_column];
		});
	}

	/**
	 * Creates or updates a client credentials record with username/password and some other details that go in `metadata`
	 */
	saveOrUpdateConnection() {
		let verb = 'post';
		let endpoint = '/v2/credentials';
		let data = this.integration;

		this.resetStatusMessages();
		if (!data) {
			this.servicenowConnectionStatus = 'Missing data';
			this.connectionError = 'Unable to connect';
			return false;
		}

		data.metadata.mappings = data.metadata.mappings || {};
		data.metadata.mappings.published_by = this.selectedSharprUser.id;
		data.type = 'servicenow';
		data.values = data.password || '';
		delete data.password;
		data.is_active = 1;
		this.resetStatusMessages();
		if (this.haveCredentials) {
			verb = 'patch';
			endpoint += '/' + this.integration.id;
		}
		this.Api.request(verb, endpoint, data).then(resp => {
			this.verifyingCredentials = true;
			this.haveCredentials = true;
			if (verb === 'post') {
				this.integration.id = resp.headers('API-New-Record-Id');
			}
			// try out the creds
			this.verifyServicenowConnection();
		});
	}

	/**
	 * Saves mappings and other metadata details
	 */
	saveMappings() {
		const endpoint = `/v2/credentials/${this.integration.id}`;
		if (!this.integration.metadata.mappings.headline_column) {
			alert('Please choose a value for the headline column.');
			return;
		}
		// clear success message if it is present
		this.mappingsSuccess = undefined;
		this.integration.metadata.mappings.source_name =
			this.integration.metadata.mappings.source_name || this.samplePost.source_name;
		this.Api.request('patch', endpoint, {
			metadata_only: true,
			metadata: this.integration.metadata,
		}).then(resp => {
			this.mappingsResult = resp.status == 204 ? 'success' : 'error';
			// if this is their first save and they want to get existing ServiceNow posts, start fetching them.
			if (this.haveExistingIntegration == false && this.integration.metadata.get_existing_servicenow_data == 'true') {
				this.haveExistingIntegration = true;
				this.Api.post(
					`/v2/servicenow/${this.integration.id}/begin_initial_import`,
					{ workflow_state: 'published' },
					{ 'Submit-As-Job': 'true' }
				);
			}
		});
	}
}
